import type { ReceiveConfirmLineDto } from '@/entities/receive';

import type { ConfirmArrivalLineValues, ConfirmArrivalQuantityField, ConfirmArrivalUnitMode } from '../model/types';
import { CONFIRM_ARRIVAL_UNIT_MODE } from '../model/constants';

/** Total the backend will accept across all three condition buckets. */
export const getExpectedTotal = (line: ReceiveConfirmLineDto): number => line.expectedGoodQty + line.expectedDamagedQty;

/**
 * Which fields give up units when another one needs room, in order.
 *
 * Good is always the first donor — it holds the default full expectation, so
 * recording damage or a loss should come out of it before anything else. Only
 * once Good is empty does the remaining bucket pay, which is the "increment
 * Damaged with no Good left → take from Lost, and vice versa" rule.
 */
const DONOR_ORDER: Record<ConfirmArrivalQuantityField, ConfirmArrivalQuantityField[]> = {
  goodQty: ['damagedQty', 'lostQty'],
  damagedQty: ['goodQty', 'lostQty'],
  lostQty: ['goodQty', 'damagedQty']
};

/**
 * The most a single field may hold.
 *
 * Damaged and Lost are bounded only by the line's total expectation, but Good
 * carries the backend's separate `goodQty <= expectedGoodQty` rule — which bites
 * whenever part of the shipment was already expected damaged.
 */
export const getFieldCeiling = (line: ReceiveConfirmLineDto, field: ConfirmArrivalQuantityField): number => {
  const total = getExpectedTotal(line);
  return field === 'goodQty' ? Math.min(line.expectedGoodQty, total) : total;
};

/**
 * Applies one edit to a line and rebalances the rest so the three buckets never
 * sum above the expectation — the redistribution the JSF page did in the
 * browser, restored.
 *
 * Only **overflow** moves. Lowering a field frees capacity and touches nothing
 * else, which is what makes a partial receipt expressible: Good 19 → 8 simply
 * leaves 11 outstanding rather than pushing 11 into another bucket.
 *
 * The edited field is clamped to its own ceiling first, which is what guarantees
 * the donors can absorb the rest: after clamping `next <= total`, so
 * `overflow = next + others - total <= others`.
 */
export const redistributeQuantities = (
  line: ReceiveConfirmLineDto,
  current: ConfirmArrivalLineValues,
  field: ConfirmArrivalQuantityField,
  rawNext: number
): ConfirmArrivalLineValues => {
  const total = getExpectedTotal(line);
  const next = Math.max(0, Math.min(Math.round(rawNext), getFieldCeiling(line, field)));

  const result: ConfirmArrivalLineValues = { ...current, [field]: next };
  let overflow = result.goodQty + result.damagedQty + result.lostQty - total;

  for (const donor of DONOR_ORDER[field]) {
    if (overflow <= 0) break;
    const taken = Math.min(result[donor], overflow);
    result[donor] -= taken;
    overflow -= taken;
  }

  return result;
};

/** What is still outstanding once the operator's entry is subtracted. */
export const getRemainingQty = (line: ReceiveConfirmLineDto, values: ConfirmArrivalLineValues): number =>
  getExpectedTotal(line) - (values.goodQty + values.damagedQty + values.lostQty);

export const isLineEmpty = (values: ConfirmArrivalLineValues): boolean =>
  values.goodQty === 0 && values.damagedQty === 0 && values.lostQty === 0;

/**
 * Whether the line can be typed in handling units. A missing or non-positive
 * `ltusPerBundle` would make the conversion meaningless, so it disables bundle
 * entry just as `bundleQtyAllowed: false` does.
 */
export const supportsHandlingUnits = (line: ReceiveConfirmLineDto): boolean =>
  line.bundleQtyAllowed && Number.isFinite(line.ltusPerBundle) && line.ltusPerBundle > 0;

/**
 * Inventory units → handling units, for display in bundle mode. Deliberately
 * unrounded: `disposition.bundlewarn.desc` warns the user that bundle figures
 * are estimates, and rounding here would silently change what they typed.
 */
export const toHandlingUnits = (line: ReceiveConfirmLineDto, inventoryQty: number): number =>
  supportsHandlingUnits(line) ? inventoryQty / line.ltusPerBundle : inventoryQty;

/**
 * Handling units → inventory units, rounded to a whole LTU count because that is
 * the only thing the API accepts.
 */
export const toInventoryUnits = (line: ReceiveConfirmLineDto, handlingQty: number): number =>
  supportsHandlingUnits(line) ? Math.round(handlingQty * line.ltusPerBundle) : handlingQty;

/** Converts a displayed value back to the inventory units the form stores. */
export const fromDisplayQty = (line: ReceiveConfirmLineDto, mode: ConfirmArrivalUnitMode, value: number): number =>
  mode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING ? toInventoryUnits(line, value) : value;

/** Converts a stored inventory-unit value to what the input shows. */
export const toDisplayQty = (line: ReceiveConfirmLineDto, mode: ConfirmArrivalUnitMode, value: number): number =>
  mode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING ? toHandlingUnits(line, value) : value;

/**
 * Trims the float noise a unit conversion leaves behind: 100 inventory units at
 * 3 per bundle is 33.333333333333336, which no quantity cell should ever print.
 *
 * Display only — the form keeps the unrounded value, so switching modes back and
 * forth cannot walk a quantity away from what the operator entered.
 */
export const formatQty = (value: number): string => String(Math.round(value * 100) / 100);

/** Unit label shown next to a quantity, per mode. */
export const getUnitLabel = (line: ReceiveConfirmLineDto, mode: ConfirmArrivalUnitMode): string =>
  mode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING && supportsHandlingUnits(line)
    ? (line.bundleTypeDescription ?? line.ltuTypeDescription)
    : line.ltuTypeDescription;

/**
 * The legacy dialog's "Handling quantity summary" banner: the receipt's total in
 * handling units, per bundle type. Lines that carry no usable bundle
 * configuration contribute nothing — matching JSF, which shows no bundle count
 * when `bundleQtyAllowed` is false.
 */
export const buildHandlingSummary = (
  lines: ReceiveConfirmLineDto[],
  values: ConfirmArrivalLineValues[]
): { label: string; quantity: number }[] => {
  const valuesByLineId = new Map(values.map((line) => [line.lineId, line]));
  const totals = new Map<string, number>();

  lines.forEach((line) => {
    if (!supportsHandlingUnits(line)) return;
    const entered = valuesByLineId.get(line.lineId);
    if (!entered) return;

    const inventoryQty = entered.goodQty + entered.damagedQty + entered.lostQty;
    if (inventoryQty <= 0) return;

    const label = line.bundleTypeDescription ?? line.ltuTypeDescription;
    totals.set(label, (totals.get(label) ?? 0) + inventoryQty / line.ltusPerBundle);
  });

  return [...totals.entries()].map(([label, quantity]) => ({
    label,
    quantity: Math.round(quantity * 100) / 100
  }));
};
